/**
 * Scrubbing for client error reports (ADR 0067).
 *
 * An error message is written by whoever threw it, and in a mail client that
 * can be anything: a subject quoted into a parse error, an address in a
 * validation message, a URL with a token in its query string. Nothing leaves
 * the browser until it has been through here.
 *
 * The Go side (`backend/internal/errreport`) scrubs again with the same rules,
 * and both are held to ONE shared fixture
 * (`backend/internal/errreport/testdata/scrub_vectors.json`), so a rule
 * changed here without its twin fails a test on one side or the other.
 *
 * The rules are deliberately blunt. A report that has lost a detail is still a
 * report; a report that carried a subject line is a leak.
 */

/** Longest input looked at at all: the regexes stay cheap on a runaway string. */
const MAX_INPUT = 20_000;

/** Default cap on the scrubbed result. */
export const DEFAULT_SCRUB_LIMIT = 4000;

const BEARER = /Bearer\s+\S+/g;
const JWT = /eyJ[\w-]+\.[\w-]+\.[\w-]+/g;
const EMAIL = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;
/**
 * Absolute http(s) URLs, and root-relative API paths. A relative path is only
 * taken when it starts a token, so the `/api/` inside an absolute URL (already
 * rewritten by then) is never matched twice.
 */
const URL_LIKE = /https?:\/\/[^\s)'"`<>]+|(?<![\w.:/-])\/api\/[^\s)'"`<>]*/g;
const KEY_VALUE =
  /(password|passwd|pwd|token|secret|api[_-]?key|authorization|auth|cookie|session[_-]?id)\s*[:=]\s*\S+/gi;
const QUOTED = /"([^"\n]{0,500})"|'([^'\n]{0,500})'|`([^`\n]{0,500})`/g;
const IDENTIFIER = /^[A-Za-z_$][\w$.-]{0,40}$/;
const HEX_RUN = /[0-9a-fA-F]{24,}/g;
const TOKEN_RUN = /[A-Za-z0-9_-]{32,}/g;
const LONG_NUMBER = /(?<![:\d])\d{5,}/g;
const HAS_DIGIT = /\d/;

/**
 * A path segment worth keeping: a route word of letters and hyphens (`api`,
 * `messages`, `out-of-office`), a version (`v1`), or a bundle file name
 * (`MailView-Cq9x.js`). Anything else is an id -- including an id that happens
 * to be lowercase (`acc_9f8e7d6c5b4a`), which is why route words carry no
 * digits or underscores.
 */
const PATH_WORD = /^[a-z][a-z-]{0,23}$/;
const PATH_VERSION = /^v[0-9]{1,2}$/;
const FILE_NAME = /^[A-Za-z0-9_.-]{1,64}\.(?:js|mjs|cjs|css|map|html|ts|tsx|jsx|json|webmanifest)$/;
/** A stack frame's position (`:line` or `:line:col`) on the end of a URL. */
const LINE_COL = /(?::\d+)+$/;

function keepSegment(segment: string): boolean {
  return segment === '' || PATH_WORD.test(segment) || PATH_VERSION.test(segment) || FILE_NAME.test(segment);
}

function scrubPath(path: string): string {
  return path
    .split('/')
    .map((segment) => (keepSegment(segment) ? segment : ':id'))
    .join('/');
}

/**
 * One URL: the query, fragment and any userinfo go, the scheme and host stay,
 * and every path segment that is not a route word or a file name becomes
 * `:id`. A stack frame's `:line:col` is kept, whether it sat on the path or
 * after the query.
 */
function scrubUrl(url: string): string {
  const cut = url.search(/[?#]/);
  let rest = cut === -1 ? url : url.slice(0, cut);
  const dropped = cut === -1 ? '' : url.slice(cut);
  let lineCol = LINE_COL.exec(dropped)?.[0] ?? '';
  if (!lineCol) {
    lineCol = LINE_COL.exec(rest)?.[0] ?? '';
    rest = rest.slice(0, rest.length - lineCol.length);
  }

  // Scheme and host only: `user:password@` in front of the host goes too.
  const origin = /^(https?:\/\/)(?:[^/@]*@)?([^/]*)/.exec(rest);
  if (!origin) return scrubPath(rest) + lineCol;
  return `${origin[1]}${origin[2]}${scrubPath(rest.slice(origin[0].length))}${lineCol}`;
}

/** Scrub free text for a client error report, then cap it at `limit` chars. */
export function scrubText(s: string, limit: number = DEFAULT_SCRUB_LIMIT): string {
  let out = String(s).slice(0, MAX_INPUT);
  out = out.replace(BEARER, 'Bearer <token>');
  out = out.replace(JWT, '<token>');
  // URLs before key=value: `?token=abc` redacted first would end the URL match
  // at its `<` and leave a `#fragment` behind. Same order as the Go side.
  out = out.replace(URL_LIKE, scrubUrl);
  out = out.replace(KEY_VALUE, '$1=<redacted>');
  out = out.replace(EMAIL, '<email>');
  out = out.replace(QUOTED, (whole: string, dq?: string, sq?: string, bq?: string) => {
    const content = dq ?? sq ?? bq ?? '';
    if (IDENTIFIER.test(content)) return whole;
    const quote = whole[0] ?? '"';
    return `${quote}<str>${quote}`;
  });
  out = out.replace(HEX_RUN, (run) => (HAS_DIGIT.test(run) ? '<hex>' : run));
  out = out.replace(TOKEN_RUN, (run) => (HAS_DIGIT.test(run) ? '<token>' : run));
  out = out.replace(LONG_NUMBER, '<n>');
  return out.length > limit ? out.slice(0, limit) : out;
}
